/**
 * =============================================================================
 * Componente: AffiliatePageHeader
 * Versão: 2.0.0
 *
 * Descrição:
 *   Cabeçalho padronizado das páginas internas do painel do afiliado.
 *
 * Mudanças v2.0.0:
 *   - Subtítulo NÃO é mais exibido inline (ganhamos espaço vertical).
 *   - Quando há `subtitle`, renderizamos um botão "?" ao lado do título
 *     que abre um Popover com a nota explicativa.
 * =============================================================================
 */
import { ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface AffiliatePageHeaderProps {
  /** Título principal da página. */
  title: string;
  /** Nota explicativa opcional — exibida apenas via Popover do botão "?". */
  subtitle?: string;
  /** Ícone decorativo opcional, exibido em cápsula gradiente à esquerda. */
  icon?: ReactNode;
  /** Ações no canto direito (botões, etc.). */
  actions?: ReactNode;
  className?: string;
}

export function AffiliatePageHeader({
  title,
  subtitle,
  icon,
  actions,
  className,
}: AffiliatePageHeaderProps) {
  return (
    <header
      className={cn(
        'flex items-center justify-between gap-3',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        {icon && (
          <div
            className="flex h-11 w-11 items-center justify-center rounded-2xl text-[hsl(var(--aff-primary-foreground))] shrink-0"
            style={{ background: 'var(--aff-gradient-hero)' }}
          >
            {icon}
          </div>
        )}
        <div className="flex min-w-0 items-center gap-1.5">
          <h1 className="text-[22px] md:text-[26px] font-bold leading-tight tracking-tight text-[hsl(var(--aff-foreground))] truncate">
            {title}
          </h1>
          {subtitle && (
            // Botão "?" — exibe a nota explicativa em Popover, liberando espaço
            // vertical no header (não há mais subtítulo inline).
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="Sobre esta página"
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[hsl(var(--aff-muted-foreground))] transition-colors hover:bg-[hsl(var(--aff-surface-2))] hover:text-[hsl(var(--aff-foreground))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--aff-primary))]"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="bottom"
                align="start"
                className="w-72 text-[13px] leading-relaxed text-[hsl(var(--aff-foreground))]"
              >
                {subtitle}
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </header>
  );
}
